import { memo } from 'react'
import { HAN_FONT, inkDrop } from './hanText'
import { FLOWER_HAN, HAN_NUM, HONOR_HAN, faceOfKind, kindOf, type TileFace } from './mahjongTiles'

/**
 * ONE MAHJONG TILE, DRAWN — owner, 2026-10-26: 開始做麻將. An ivory face on a green back, the way the tiles on a
 * Taiwanese table look: characters in blue-black numerals over a red 萬, dots in their classic patterns, bamboo
 * sticks, the winds in black, 中 red, 發 green, 白 an empty blue frame, and the flowers with their number.
 *
 * Drawn in a 30 × 40 box; the size is the caller's (CSS). Every character is set on its INK, not its font's idea
 * of a middle (hanText.ts — the owner's iPhone showed what `dominant-baseline` does to Chinese text), so it sits
 * where it is meant to on every phone.
 */

const INK = '#1d2748'
const RED = '#b8261d'
const GREEN = '#1f6f45'
const BLUE = '#1f4f9a'

/** The face's own box: 30 wide, 37 tall above the tile's green edge. */
const FACE_H = 37

function Han({ ch, x, y, size, fill, weight = 700 }: { ch: string; x: number; y: number; size: number; fill: string; weight?: number }) {
  return (
    <text
      x={x}
      y={y + inkDrop(ch, weight) * size}
      fontSize={size}
      fontWeight={weight}
      fontFamily={HAN_FONT}
      fill={fill}
      textAnchor="middle"
    >
      {ch}
    </text>
  )
}

/** A dot of 筒: a coloured ring with a pale eye. */
function Dot({ cx, cy, r, c }: { cx: number; cy: number; r: number; c: string }) {
  return (
    <g>
      <circle cx={cx} cy={cy} r={r} fill={c} />
      <circle cx={cx} cy={cy} r={r * 0.62} fill="none" stroke="#fdf8ee" strokeWidth={r * 0.18} />
      <circle cx={cx} cy={cy} r={r * 0.22} fill="#fdf8ee" />
    </g>
  )
}

const B = BLUE
const G = GREEN
const R = RED
/** Where each of one to nine dots goes: [x, y, colour, radius]. */
const DOTS: ReadonlyArray<ReadonlyArray<[number, number, string, number]>> = [
  [],
  [
    [15, 10.5, G, 5],
    [15, 26.5, B, 5],
  ],
  [
    [8.5, 8, B, 4.2],
    [15, 18.5, R, 4.2],
    [21.5, 29, G, 4.2],
  ],
  [
    [9.5, 11, B, 4.3],
    [20.5, 11, G, 4.3],
    [9.5, 26, G, 4.3],
    [20.5, 26, B, 4.3],
  ],
  [
    [8.5, 8.5, B, 3.8],
    [21.5, 8.5, G, 3.8],
    [15, 18.5, R, 3.8],
    [8.5, 28.5, G, 3.8],
    [21.5, 28.5, B, 3.8],
  ],
  [
    [10, 8, G, 3.6],
    [20, 8, G, 3.6],
    [10, 19, R, 3.6],
    [20, 19, R, 3.6],
    [10, 29, R, 3.6],
    [20, 29, R, 3.6],
  ],
  [
    [7.5, 6, G, 2.9],
    [15, 9.5, G, 2.9],
    [22.5, 13, G, 2.9],
    [10, 21, R, 3.2],
    [20, 21, R, 3.2],
    [10, 30, R, 3.2],
    [20, 30, R, 3.2],
  ],
  [
    [10, 6, B, 3.2],
    [20, 6, B, 3.2],
    [10, 14.3, B, 3.2],
    [20, 14.3, B, 3.2],
    [10, 22.6, B, 3.2],
    [20, 22.6, B, 3.2],
    [10, 31, B, 3.2],
    [20, 31, B, 3.2],
  ],
  [
    [7.5, 7.5, B, 3.2],
    [15, 7.5, B, 3.2],
    [22.5, 7.5, B, 3.2],
    [7.5, 18.5, R, 3.2],
    [15, 18.5, R, 3.2],
    [22.5, 18.5, R, 3.2],
    [7.5, 29.5, G, 3.2],
    [15, 29.5, G, 3.2],
    [22.5, 29.5, G, 3.2],
  ],
]

/** A bamboo stick of 條, centred on (cx, cy). */
function Stick({ cx, cy, h = 9, c = GREEN }: { cx: number; cy: number; h?: number; c?: string }) {
  const w = 3.2
  return (
    <g>
      <rect x={cx - w / 2} y={cy - h / 2} width={w} height={h} rx={1.4} fill={c} />
      <rect x={cx - w / 2} y={cy - 0.45} width={w} height={0.9} fill="#fdf8ee" opacity={0.85} />
      <rect x={cx - 0.4} y={cy - h / 2 + 1.2} width={0.8} height={h - 2.4} fill="#fdf8ee" opacity={0.35} />
    </g>
  )
}

/** Where each of two to nine sticks goes: [x, y, red?]. */
const STICKS: ReadonlyArray<ReadonlyArray<[number, number, boolean]>> = [
  [],
  [],
  [
    [15, 10.5, false],
    [15, 26.5, false],
  ],
  [
    [15, 10.5, false],
    [9.5, 26.5, false],
    [20.5, 26.5, false],
  ],
  [
    [9.5, 10.5, false],
    [20.5, 10.5, false],
    [9.5, 26.5, false],
    [20.5, 26.5, false],
  ],
  [
    [8, 9.5, false],
    [22, 9.5, false],
    [15, 18.5, true],
    [8, 27.5, false],
    [22, 27.5, false],
  ],
  [
    [7.5, 10.5, false],
    [15, 10.5, false],
    [22.5, 10.5, false],
    [7.5, 26.5, false],
    [15, 26.5, false],
    [22.5, 26.5, false],
  ],
  [
    [15, 6.5, true],
    [7.5, 18, false],
    [15, 18, false],
    [22.5, 18, false],
    [7.5, 29.5, false],
    [15, 29.5, false],
    [22.5, 29.5, false],
  ],
  [
    [6, 10.5, false],
    [12, 10.5, false],
    [18, 10.5, false],
    [24, 10.5, false],
    [6, 26.5, false],
    [12, 26.5, false],
    [18, 26.5, false],
    [24, 26.5, false],
  ],
  [
    [7.5, 7, false],
    [15, 7, true],
    [22.5, 7, false],
    [7.5, 18.5, false],
    [15, 18.5, true],
    [22.5, 18.5, false],
    [7.5, 30, false],
    [15, 30, true],
    [22.5, 30, false],
  ],
]

const FLOWER_INK = ['#2f7d32', '#c0392b', '#d35400', '#1f4f9a', '#b8261d', '#7b3fa0', '#1f6f45', '#c77c02'] as const

function Face({ f }: { f: TileFace }) {
  if ('suit' in f) {
    if (f.suit === 'm') {
      return (
        <>
          <Han ch={HAN_NUM[f.n - 1]!} x={15} y={11.5} size={12.5} fill={INK} />
          <Han ch="萬" x={15} y={27} size={13} fill={RED} />
        </>
      )
    }
    if (f.suit === 'p') {
      if (f.n === 1) {
        return (
          <g>
            <circle cx={15} cy={18.5} r={11} fill={GREEN} />
            <circle cx={15} cy={18.5} r={8.6} fill="#fdf8ee" />
            <circle cx={15} cy={18.5} r={7.2} fill={RED} />
            <circle cx={15} cy={18.5} r={4.2} fill="#fdf8ee" />
            <circle cx={15} cy={18.5} r={2.6} fill={BLUE} />
          </g>
        )
      }
      return (
        <>
          {DOTS[f.n - 1]!.map(([x, y, c, r], i) => (
            <Dot key={i} cx={x} cy={y} r={r} c={c} />
          ))}
        </>
      )
    }
    if (f.n === 1) {
      // 一條: one tall stick, capped in red.
      return (
        <g>
          <Stick cx={15} cy={18.5} h={27} />
          <circle cx={15} cy={5.5} r={2.6} fill={RED} />
          <circle cx={15} cy={31.5} r={2.6} fill={RED} />
        </g>
      )
    }
    return (
      <>
        {STICKS[f.n]!.map(([x, y, red], i) => (
          <Stick key={i} cx={x} cy={y} h={f.n >= 7 ? 8.6 : 9.6} c={red ? RED : GREEN} />
        ))}
      </>
    )
  }
  if ('honor' in f) {
    if (f.honor === 'P') {
      return (
        <g fill="none" stroke={BLUE}>
          <rect x={6} y={6} width={18} height={25} rx={2} strokeWidth={2} />
          <rect x={8.8} y={8.8} width={12.4} height={19.4} rx={1} strokeWidth={0.8} />
        </g>
      )
    }
    const fill = f.honor === 'C' ? RED : f.honor === 'F' ? GREEN : INK
    return <Han ch={HONOR_HAN[f.honor]!} x={15} y={FACE_H / 2} size={21} fill={fill} />
  }
  const c = FLOWER_INK[f.flower]!
  return (
    <>
      <Han ch={FLOWER_HAN[f.flower]!} x={15} y={20.5} size={17} fill={c} />
      <text x={5} y={8} fontSize={6.5} fontWeight={700} fill={c} textAnchor="middle" fontFamily="system-ui, sans-serif">
        {(f.flower % 4) + 1}
      </text>
    </>
  )
}

/**
 * A tile: `tile` face up, or (null) its back. `className` sizes it; `title` names it for a reader of the screen.
 */
export const MahjongTile = memo(function MahjongTile({
  tile,
  className,
  title,
}: {
  tile: number | null
  className?: string | undefined
  title?: string | undefined
}) {
  return (
    <svg className={`mj-tile${className ? ` ${className}` : ''}`} viewBox="0 0 30 40" role="img" aria-label={title} aria-hidden={title ? undefined : true}>
      <rect x={0} y={1.5} width={30} height={38.5} rx={4} fill="#2c6e50" />
      {tile === null ? (
        <>
          <rect x={0} y={0} width={30} height={FACE_H} rx={4} fill="#3a8a63" />
          <rect x={3} y={3} width={24} height={FACE_H - 6} rx={2.5} fill="none" stroke="#a7d3b8" strokeWidth={0.8} opacity={0.7} />
        </>
      ) : (
        <>
          <rect x={0} y={0} width={30} height={FACE_H} rx={4} fill="#fbf6e9" />
          <rect x={0.4} y={0.4} width={29.2} height={FACE_H - 0.8} rx={3.6} fill="none" stroke="#d8ccb0" strokeWidth={0.8} />
          <Face f={faceOfKind(kindOf(tile))} />
        </>
      )}
    </svg>
  )
})
